import { useQueryClient } from "@tanstack/react-query";
import { ContractSearchForm } from "@/components/contracts/ContractSearchForm";
import { ContractSearchResults } from "@/components/contracts/ContractSearchResults";
import { LastSearchBanner } from "@/components/shared/last-search-banner";
import { PageHeader } from "@/components/shared/page-header";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { type ActiveBuInstance, useActiveBuInstances } from "@/hooks/use-bu-instances";
import {
  useClearLastContractSearch,
  useLastContractSearch,
  useSearchContracts,
} from "@/hooks/use-contract-search";
import type { SearchContractsResult } from "@/lib/coach-manager/client";
import { contractSearchKeys } from "@/lib/query";
import type { ContractSearchRequest } from "@/schemas/contracts";

const OPERATOR_LABELS: Record<string, string> = {
  GreaterThan: "after",
  EqualTo: "on",
  LessThan: "before",
};

function buildCriteriaFields(
  criteria: ContractSearchRequest,
  buInstances: ActiveBuInstance[] | undefined,
  buInstanceLabel: string,
): Array<{ label: string; value: string }> {
  const bu = buInstances?.find((b) => b.id === criteria.buInstanceId);
  const fields: Array<{ label: string; value: string }> = [
    { label: buInstanceLabel, value: bu?.name ?? criteria.buInstanceId },
  ];

  if (criteria.contractId != null)
    fields.push({ label: "Contract ID", value: String(criteria.contractId) });
  if (criteria.contractMovementId != null) {
    fields.push({ label: "Movement ID", value: String(criteria.contractMovementId) });
  }
  if (criteria.clientId) fields.push({ label: "Client ID", value: criteria.clientId });
  if (criteria.surname) fields.push({ label: "Surname", value: criteria.surname });
  if (criteria.company) fields.push({ label: "Company", value: criteria.company });
  if (criteria.initials) fields.push({ label: "Initials", value: criteria.initials });
  if (criteria.pickup) fields.push({ label: "Pickup", value: criteria.pickup });
  if (criteria.destination) fields.push({ label: "Destination", value: criteria.destination });
  if (criteria.clientReference1) {
    fields.push({ label: "Client Reference 1", value: criteria.clientReference1 });
  }
  if (criteria.clientReference2) {
    fields.push({ label: "Client Reference 2", value: criteria.clientReference2 });
  }
  if (criteria.postCode) fields.push({ label: "Post Code", value: criteria.postCode });
  if (criteria.dateCreated) {
    const op = criteria.dateCreatedOperator
      ? OPERATOR_LABELS[criteria.dateCreatedOperator]
      : undefined;
    fields.push({
      label: "Date Created",
      value: op ? `${op} ${criteria.dateCreated}` : criteria.dateCreated,
    });
  }
  if (criteria.lastUpdated) {
    const op = criteria.lastUpdatedOperator
      ? OPERATOR_LABELS[criteria.lastUpdatedOperator]
      : undefined;
    fields.push({
      label: "Last Updated",
      value: op ? `${op} ${criteria.lastUpdated}` : criteria.lastUpdated,
    });
  }

  return fields;
}

export function ContractSearchPage() {
  const queryClient = useQueryClient();
  const { label } = useBuInstanceLabel();
  const { data: buInstances } = useActiveBuInstances();
  const search = useSearchContracts();
  const lastSearch = useLastContractSearch();
  const clearLastSearch = useClearLastContractSearch();

  function handleSearch(values: ContractSearchRequest) {
    search.mutate(values, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: contractSearchKeys.last });
      },
    });
  }

  function handleClear() {
    clearLastSearch.mutate();
    search.reset();
  }

  // Bridges the gap between "mutation resolved" and "invalidated lastSearch
  // query refetching" so old results don't flash before the fresh ones land —
  // pure derived state, no extra useState.
  const isPending = search.isPending || (search.isSuccess && lastSearch.isFetching);

  const state = lastSearch.data;
  const result: SearchContractsResult | undefined = state
    ? state.status === "success"
      ? { ok: true, contracts: state.contracts ?? [], resultDescription: "" }
      : { ok: false, error: state.errorMessage ?? "Search failed" }
    : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contract Search"
        description={`Search a Coach Manager ${label} for a Contract and view its full detail.`}
      />
      <ContractSearchForm onSearch={handleSearch} isSearching={search.isPending} />
      {state && !isPending && (
        <LastSearchBanner
          fields={buildCriteriaFields(state.criteria, buInstances, label)}
          searchedAt={state.searchedAt}
          onClear={handleClear}
          isClearing={clearLastSearch.isPending}
        />
      )}
      <ContractSearchResults
        result={result}
        isPending={isPending}
        error={search.error ?? lastSearch.error}
        buInstanceId={state?.criteria.buInstanceId ?? null}
      />
    </div>
  );
}
